import type { BeforeSendFn, Properties } from 'posthog-js'
import config from '@/config'
import accountConfig from '@/modules/account/config'
import settingsConfig from '@/modules/settings/config'

/**
 * Keeps the `$exception` volume (and with it the PostHog bill) focused on real problems.
 *
 * Two kinds of errors are part of the normal flow and must not be reported as exceptions:
 *   - requests that fail by design, e.g. the session/token endpoints with an expired token
 *   - everything the app logs via `console.error` after it already handled the error
 *     (`capture_console_errors` is off in `config.ts` for that reason)
 *
 * Errors the user actually sees are tracked through the "Toast shown" event instead. The
 * properties built here describe such an error without any free text (toast messages and API
 * error messages may contain customer data).
 */

/** Endpoints whose failures are expected (expired session / token) and never a bug. */
export const IGNORED_REQUEST_ERROR_ENDPOINTS: readonly string[] = [
  accountConfig.endpoints.trackingInformation,
  settingsConfig.refreshToken,
]

const stripQuery = (url: string) => url.split('?')[0]

/**
 * Browser messages that reach `window.onerror` without being a bug. "ResizeObserver loop …" only
 * says the browser postponed some resize notifications to the next frame because an observer
 * callback changed the layout again (ag-grid, MUI, our own observers). It has no stack, nothing
 * breaks, and it fired on almost every page. The `error` listener in `src/index.js` cannot hold it
 * back from PostHog: the SDK wraps `window.onerror`, whose slot is registered before that listener
 * as soon as any earlier script assigned it.
 */
export const IGNORED_EXCEPTION_MESSAGES: readonly string[] = [
  'ResizeObserver loop completed with undelivered notifications',
  'ResizeObserver loop limit exceeded',
]

const exceptionMessages = (properties: Properties = {}): string[] => {
  const list = Array.isArray(properties.$exception_list) ? properties.$exception_list : []
  return [...list.map(entry => entry?.value), properties.$exception_message]
    .filter((message): message is string => typeof message === 'string')
}

/** `before_send` hook: drops `$exception` events of the benign browser messages above. */
export const dropIgnoredExceptions: BeforeSendFn = (event) => {
  if (event?.event !== '$exception') {
    return event
  }
  const ignored = exceptionMessages(event.properties)
    .some(message => IGNORED_EXCEPTION_MESSAGES.some(ignoredMessage => message.includes(ignoredMessage)))
  return ignored ? null : event
}

/** `true` when a failed request to `endpoint` must not be sent to PostHog as `$exception`. */
export const isRequestErrorIgnored = (endpoint: string, ignoredEndpoints = IGNORED_REQUEST_ERROR_ENDPOINTS): boolean => {
  const path = stripQuery(endpoint)
  return ignoredEndpoints.some(ignored => path === stripQuery(ignored))
}

const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24,})$/i

/**
 * Reduces a request URL to a route-like path: host and query string are removed, numeric and
 * uuid-like segments are replaced by `:id`, so the same route groups together in PostHog.
 */
export const normalizeEndpoint = (url: string): string => {
  let path = stripQuery(url)
  if (path.startsWith(config.resourceHost)) {
    path = path.slice(config.resourceHost.length)
  } else {
    path = path.replace(/^[a-z]+:\/\/[^/]+\//i, '')
  }
  return path
    .split('/')
    .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
    .join('/')
}

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null

/** First `code` found in the API error body (`meta.<field>[n].code` or a top level `code`). */
const findErrorCode = (value: unknown, depth = 0): string | number | undefined => {
  if (!isRecord(value) || depth > 4) {
    return undefined
  }
  if (typeof value.code === 'string' || typeof value.code === 'number') {
    return value.code
  }
  for (const child of Object.values(value)) {
    const entries = Array.isArray(child) ? child : [child]
    for (const entry of entries) {
      const code = findErrorCode(entry, depth + 1)
      if (code !== undefined) {
        return code
      }
    }
  }
  return undefined
}

/**
 * Properties describing an error shown in a toast. Accepts the shapes that reach
 * `toast.showToast({ error })`: the rejection of `request()`, a raw API error body or an `Error`.
 */
export const buildToastErrorProperties = (error: unknown): Properties => {
  if (!isRecord(error)) {
    return {}
  }
  const properties: Properties = {}

  const response = isRecord(error.response) ? error.response : undefined
  const statusCode = error.statusCode ?? response?.status
  if (typeof statusCode === 'number') {
    properties.errorStatusCode = statusCode
  }

  const body = isRecord(error.body) ? error.body : error
  if (typeof body.name === 'string') {
    properties.errorName = body.name
  } else if (error instanceof Error) {
    properties.errorName = error.name
  }

  const code = findErrorCode(body)
  if (code !== undefined) {
    properties.errorCode = code
  }

  const requestConfig = isRecord(response?.config) ? response.config : undefined
  const url = requestConfig?.url
  if (typeof url === 'string') {
    properties.errorEndpoint = normalizeEndpoint(url)
    if (typeof requestConfig?.method === 'string') {
      properties.errorMethod = requestConfig.method.toUpperCase()
    }
  }

  return properties
}
